import React, { useRef, useState, useEffect } from 'react';
import './PageTabs.css';
import cursorDefault from '../../assets/cursors/cursor-default.png';
import cursorPointer from '../../assets/cursors/cursor-pointer.png';

const PageTabs = ({
  pages,
  activePage,
  handlePageClick,
  handleDeleteClick,
  handleAddPage,
  handleMovePage,
}) => {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const tabCursorDefault = `url(${cursorDefault}) 2 2, default`;
  const tabCursorPointer = `url(${cursorPointer}) 6 2, pointer`;

  const updateScrollButtons = () => {
    const el = scrollRef.current;
    if (!el) return;

    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  };

  useEffect(() => {
    updateScrollButtons();
  }, [pages.length]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    el.addEventListener('scroll', updateScrollButtons);
    window.addEventListener('resize', updateScrollButtons);

    return () => {
      el.removeEventListener('scroll', updateScrollButtons);
      window.removeEventListener('resize', updateScrollButtons);
    };
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const activeTab = el.querySelector('.page-tab.active');
    if (activeTab) {
      activeTab.scrollIntoView({
        behavior: 'smooth',
        inline: 'center',
        block: 'nearest',
      });
    }
  }, [activePage]);

  const scrollTabs = (direction) => {
    const el = scrollRef.current;
    if (!el) return;

    el.scrollBy({
      left: direction === 'left' ? -260 : 260,
      behavior: 'smooth',
    });
  };

  const handleWheelScroll = (e) => {
    const el = scrollRef.current;
    if (!el) return;

    e.preventDefault();
    el.scrollLeft += e.deltaY || e.deltaX;
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.setData('text/plain', String(index));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();

    const fromIndex = Number(e.dataTransfer.getData('text/plain'));

    setDraggedIndex(null);
    setDragOverIndex(null);

    if (!handleMovePage) return;
    if (Number.isNaN(fromIndex)) return;
    if (fromIndex === dropIndex) return;

    handleMovePage(fromIndex, dropIndex);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  return (
    <div
      className="page-tabs-shell"
      style={{ cursor: tabCursorDefault }}
      data-tour="editor-pages"
    >
      <button
        type="button"
        className={`page-tabs-scroll-button ${!canScrollLeft ? 'disabled' : ''}`}
        onClick={() => scrollTabs('left')}
        disabled={!canScrollLeft}
        title="Scroll tabs left"
        style={{ cursor: canScrollLeft ? tabCursorPointer : tabCursorDefault }}
      >
        ‹
      </button>

      <div
        className="page-tabs"
        ref={scrollRef}
        onWheel={handleWheelScroll}
      >
        {pages.map((page, index) => (
          <div
            key={page.id}
            className={[
              'page-tab',
              activePage === index ? 'active' : '',
              draggedIndex === index ? 'dragging' : '',
              dragOverIndex === index && draggedIndex !== index ? 'drag-over' : '',
            ].join(' ')}
            onClick={() => handlePageClick(index)}
            draggable
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDrop={(e) => handleDrop(e, index)}
            onDragEnd={handleDragEnd}
            style={{ cursor: tabCursorPointer }}
            title="Drag to reorder pages"
          >
            <button
              type="button"
              className="page-tab-title"
              style={{ cursor: tabCursorPointer }}
            >
              <span className="page-tab-grip">⋮⋮</span>
                Page {index + 1}
            </button>

            <button
              type="button"
              className="page-tab-action"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteClick(page.id);
              }}
              title="Delete page"
              style={{ cursor: tabCursorPointer }}
            >
              <span className="close-icon">×</span>
            </button>
          </div>
        ))}

        <button
          type="button"
          className="page-tab page-tab-add"
          onClick={handleAddPage}
          title="Add new page"
          style={{ cursor: tabCursorPointer }}
        >
          +
        </button>
      </div>    

      <button
        type="button"
        className={`page-tabs-scroll-button ${!canScrollRight ? 'disabled' : ''}`}
        onClick={() => scrollTabs('right')}
        disabled={!canScrollRight}
        title="Scroll tabs right"
        style={{ cursor: canScrollRight ? tabCursorPointer : tabCursorDefault }}
      >
        ›
      </button>
    </div>
  );
};

export default PageTabs;